import {
  addMonths,
  getMonth,
  getYear,
  setMonth,
  setYear,
  subMonths,
} from "date-fns";
import { useState } from "react";
import type { ComponentProps } from "react";
import { DayPicker } from "react-day-picker";
import { cn } from "../lib/utils";
import { IconButton } from "./button";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

type CalendarProps = ComponentProps<typeof DayPicker> & {
  startYear?: number;
  endYear?: number;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  components: userComponents,
  startYear = getYear(new Date()) - 100,
  endYear = getYear(new Date()) + 25,
  ...props
}: CalendarProps) {
  const defaultClassNames = {
    months: "relative flex flex-col sm:flex-row gap-4",
    month: "w-full ",
    month_caption: "hidden",
    caption_label: "hidden",
    nav: "absolute top-0 flex w-full justify-between z-10",
    button_previous: "hidden",
    button_next: "hidden",
    weekday: "size-6 text-xs font-normal text-gray-700",
    day_button: "relative flex size-6 items-center justify-center rounded",
    day: "group size-6 p-1",
    range_start: "bg-blue-500 rounded text-white",
    range_end: "bg-blue-500 rounded text-white",
    range_middle: "bg-gray-50",
    outside: "text-gray-700",
    hidden: "invisible",
  };

  const mergedClassNames = Object.keys(defaultClassNames).reduce(
    (acc, key) => {
      acc[key as keyof typeof defaultClassNames] = classNames?.[
        key as keyof typeof classNames
      ]
        ? cn(
            defaultClassNames[key as keyof typeof defaultClassNames],
            classNames[key as keyof typeof classNames],
          )
        : defaultClassNames[key as keyof typeof defaultClassNames];
      return acc;
    },
    {} as typeof defaultClassNames,
  );

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const years = Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => startYear + i,
  );

  const [displayMonth, setDisplayMonth] = useState(getMonth(new Date()));
  const [displayYear, setDisplayYear] = useState(getYear(new Date()));

  const handleMonthChange = (month: string) => {
    setDisplayMonth(months.indexOf(month));
  };

  const handleYearChange = (year: string) => {
    setDisplayYear(Number.parseInt(year));
  };

  const incrementMonth = () => {
    const newDate = addMonths(
      setYear(setMonth(new Date(), displayMonth), displayYear),
      1,
    );
    setDisplayMonth(getMonth(newDate));
    setDisplayYear(getYear(newDate));
  };

  const decrementMonth = () => {
    const newDate = subMonths(
      setYear(setMonth(new Date(), displayMonth), displayYear),
      1,
    );
    setDisplayMonth(getMonth(newDate));
    setDisplayYear(getYear(newDate));
  };

  const monthDate = setYear(setMonth(new Date(), displayMonth), displayYear);

  return (
    <div className={cn("w-fit", className)}>
      <div className="flex items-center gap-2 mb-2 justify-center">
        <IconButton
          variant="ghost"
          className="size-5.5"
          onClick={decrementMonth}
          aria-label="Previous month"
          icon="arrow-left"
        />
        <Select onValueChange={handleMonthChange} value={months[displayMonth]}>
          <SelectTrigger variant="secondary" className="w-[100px]">
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent
            variant="secondary"
            className="max-h-[200px] overflow-y-auto pink-scroll"
          >
            {months.map((month) => (
              <SelectItem key={month} value={month}>
                {month}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select onValueChange={handleYearChange} value={displayYear.toString()}>
          <SelectTrigger variant="secondary" className="w-[100px]">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent
            variant="secondary"
            className="max-h-[200px] overflow-y-auto pink-scroll"
          >
            {years.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <IconButton
          variant="ghost"
          className="size-5.5"
          onClick={incrementMonth}
          aria-label="Next month"
          icon="arrow-right"
        />
      </div>

      <DayPicker
        showOutsideDays={showOutsideDays}
        classNames={mergedClassNames}
        month={monthDate}
        {...props}
      />
    </div>
  );
}

export { Calendar };
